// Cliente HTTP de la aplicacion.
//
// Ahora que la API y las vistas son un solo site, las llamadas van a rutas
// RELATIVAS y la autenticacion viaja en la cookie de sesion: no hay tokens en el
// cliente ni header Authorization que armar.

import type { ErrorApi } from './api-types';

/** Error de la API con el status y los errores de validacion, si vinieron. */
export class ErrorHttp extends Error {
    readonly statusCode: number;
    readonly erroresValidacion: Record<string, string[]> | undefined;
    /** Discriminador del servidor para los casos en que el status no alcanza. */
    readonly codigo: string | undefined;

    constructor(
        mensaje: string,
        statusCode: number,
        erroresValidacion?: Record<string, string[]>,
        codigo?: string
    ) {
        super(mensaje);
        this.name = 'ErrorHttp';
        this.statusCode = statusCode;
        this.erroresValidacion = erroresValidacion;
        this.codigo = codigo;
    }
}

/**
 * La sesion vencio o no hay sesion. Se distingue de otros 401 por el `codigo`
 * `sesion_expirada` que manda el servidor, NO por el status: el login tambien
 * responde 401 y ahi significa "credenciales incorrectas", que no debe mandar al
 * usuario de vuelta al login ni tapar el mensaje real.
 */
export class ErrorSesionVencida extends ErrorHttp {
    constructor() {
        super('La sesión expiró. Hay que volver a iniciar sesión.', 401);
        this.name = 'ErrorSesionVencida';
    }
}

/**
 * Parametros de querystring.
 *
 * Se exporta para que las pantallas puedan declarar sus filtros con este tipo.
 * Ojo: hay que declararlos como `type` con index signature (o como Parametros),
 * NO como `interface`, porque una interface sin index signature no encaja aca.
 */
export type Parametros = Record<string, string | number | boolean | null | undefined>;

/** Arma el querystring salteando los valores vacios. */
function construirQuery(parametros?: Parametros): string {
    if (!parametros) return '';

    const query = new URLSearchParams();

    for (const [clave, valor] of Object.entries(parametros)) {
        if (valor === null || valor === undefined || valor === '') continue;
        query.append(clave, String(valor));
    }

    const texto = query.toString();
    return texto ? `?${texto}` : '';
}

/**
 * Lee el cuerpo de error que emite ExceptionHandlingMiddleware.
 *
 * Se protege contra cuerpos que no son JSON: una pagina de error del servidor o
 * una respuesta vacia no deben terminar en un "Unexpected token" que tape el
 * status real.
 */
async function leerError(respuesta: Response): Promise<ErrorHttp> {
    let mensaje = `Error ${respuesta.status} al llamar a la API.`;
    let errores: Record<string, string[]> | undefined;
    let codigo: string | undefined;

    try {
        const cuerpo = (await respuesta.json()) as ErrorApi;
        if (cuerpo?.error) mensaje = cuerpo.error;
        if (cuerpo?.errors) errores = cuerpo.errors;
        if (cuerpo?.codigo) codigo = cuerpo.codigo;
    } catch {
        // Cuerpo no-JSON o vacio: nos quedamos con el mensaje por defecto.
    }

    return new ErrorHttp(mensaje, respuesta.status, errores, codigo);
}

async function pedir<T>(url: string, init: RequestInit): Promise<T> {
    const respuesta = await fetch(url, {
        // Mismo origen: la cookie de sesion viaja sola.
        credentials: 'same-origin',
        ...init,
        headers: {
            Accept: 'application/json',
            ...(init.headers ?? {})
        }
    });

    if (!respuesta.ok) {
        const error = await leerError(respuesta);

        // Solo el 401 marcado como sesion expirada manda de vuelta al login; los
        // demas 401 (por ejemplo credenciales incorrectas) conservan su mensaje.
        throw error.statusCode === 401 && error.codigo === 'sesion_expirada'
            ? new ErrorSesionVencida()
            : error;
    }

    // 204 y cuerpos vacios: no intentar parsear.
    if (respuesta.status === 204) {
        return undefined as T;
    }

    const texto = await respuesta.text();
    return (texto ? JSON.parse(texto) : undefined) as T;
}

export function get<T>(ruta: string, parametros?: Parametros): Promise<T> {
    return pedir<T>(`${ruta}${construirQuery(parametros)}`, { method: 'GET' });
}

export function post<T>(ruta: string, cuerpo?: unknown): Promise<T> {
    return pedir<T>(ruta, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: cuerpo === undefined ? undefined : JSON.stringify(cuerpo)
    });
}

export function put<T>(ruta: string, cuerpo?: unknown): Promise<T> {
    return pedir<T>(ruta, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: cuerpo === undefined ? undefined : JSON.stringify(cuerpo)
    });
}

export function eliminar<T>(ruta: string): Promise<T> {
    return pedir<T>(ruta, { method: 'DELETE' });
}

/** Nombre de archivo del header Content-Disposition (soporta filename* y filename). */
function nombreDeContentDisposition(cabecera: string | null): string | null {
    if (!cabecera) return null;

    const codificado = /filename\*=UTF-8''([^;]+)/i.exec(cabecera);
    if (codificado?.[1]) return decodeURIComponent(codificado[1]);

    const simple = /filename="?([^";]+)"?/i.exec(cabecera);
    return simple?.[1] ?? null;
}

/**
 * Descarga un archivo (ej. un PDF) de la API y lo entrega al navegador. Va por fetch y no
 * por un <a href> directo para que, si el servidor responde error (422, 404, sesion vencida),
 * se muestre el mensaje en vez de dejar al usuario mirando un JSON en una pestaña nueva.
 */
export async function descargar(ruta: string, nombreAlternativo: string): Promise<void> {
    const respuesta = await fetch(ruta, { credentials: 'same-origin' });

    if (!respuesta.ok) {
        const error = await leerError(respuesta);

        throw error.statusCode === 401 && error.codigo === 'sesion_expirada'
            ? new ErrorSesionVencida()
            : error;
    }

    const blob = await respuesta.blob();
    const nombre = nombreDeContentDisposition(respuesta.headers.get('Content-Disposition')) ?? nombreAlternativo;

    const url = URL.createObjectURL(blob);
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = nombre;
    document.body.appendChild(enlace);
    enlace.click();
    enlace.remove();
    URL.revokeObjectURL(url);
}

/**
 * Muestra un error al usuario. Si la sesion vencio, lo manda al login en vez de
 * dejarlo mirando un mensaje sobre una pantalla que ya no puede usar.
 */
export function mostrarError(error: unknown): void {
    if (error instanceof ErrorSesionVencida) {
        window.Swal.fire({
            icon: 'warning',
            title: 'Sesión expirada',
            text: 'Tu sesión expiró. Vas a volver a la pantalla de inicio de sesión.',
            allowOutsideClick: false
        }).then(() => {
            window.location.href = '/Account/Login';
        });
        return;
    }

    const mensaje = error instanceof Error
        ? error.message
        : 'Ocurrió un error inesperado.';

    window.Swal.fire({ icon: 'error', title: 'Error', text: mensaje });
}

/** Envuelve una operacion async mostrando el error si falla. */
export async function ejecutar(operacion: () => Promise<void>): Promise<void> {
    try {
        await operacion();
    } catch (error) {
        mostrarError(error);
    }
}
